/**
 * Block 14 «Раскрой» — PR #12 (CUT-3.2). Чистые метрики ДЕТАЛЕЙ для инфо-панели
 * под таблицей (футер PartsTab). Считаются напрямую из `parts` — движок раскроя
 * не нужен (КИМ/отходы/листы — это EP-4/EP-5 на экране «Результат», НЕ здесь).
 *
 * Геометрия сторон (подтверждено по canvas-рендеру прототипа `js/app.js:635-638`:
 * `top`/`bottom` — горизонтальные линии вдоль `w`, `left`/`right` — вертикальные
 * вдоль `h`; в модели детали `w↔length`, `h↔width`):
 *   - сторона top / bottom  → длина стороны = part.length (мм)
 *   - сторона left / right   → длина стороны = part.width  (мм)
 *
 * Единицы: площадь мм²→м² (÷1e6), длины кромок/пазов мм→м (÷1000). Возвращаются
 * сырые числа; округление — в UI (площадь toFixed(2); кромка 1/2 и пазы toFixed(1)).
 *
 * @see Помощник — Прототип Раскроя/01-prototype-vanilla/project.html:365-370 (инфо-панель)
 */

import type { EdgeAssignment, EdgeType, GrooveAssignment, Part } from '../model/types';

/** Стороны детали в порядке прототипа (top, right, bottom, left). */
const SIDES = ['top', 'right', 'bottom', 'left'] as const;
type Side = (typeof SIDES)[number];

/**
 * Длина стороны детали в мм. top/bottom идут вдоль длины (part.length),
 * left/right — вдоль ширины (part.width). Геометрия — вербатим app.js:635-638.
 */
export function sideLength(part: Part, side: Side): number {
  return side === 'top' || side === 'bottom' ? part.length : part.width;
}

/** Суммарная площадь деталей, м² = Σ (length × width × qty) / 1e6. */
export function partsArea(parts: Part[]): number {
  return parts.reduce((sum, p) => sum + p.length * p.width * p.qty, 0) / 1e6;
}

/**
 * Суммарная длина кромки заданного типа (type1 = «Кромка 1», type2 = «Кромка 2»),
 * м = Σ по деталям Σ по сторонам, где edges[side]===type, (длина стороны × qty) / 1000.
 */
export function edgeLength(parts: Part[], type: Exclude<EdgeType, ''>): number {
  let mm = 0;
  for (const p of parts) {
    const edges: EdgeAssignment = p.edges;
    for (const side of SIDES) {
      if (edges[side] === type) mm += sideLength(p, side) * p.qty;
    }
  }
  return mm / 1000;
}

/**
 * Суммарная длина пазов (любой тип V/U/L), м = Σ по деталям Σ по сторонам,
 * где grooves[side] !== '', (длина стороны × qty) / 1000.
 */
export function grooveLength(parts: Part[]): number {
  let mm = 0;
  for (const p of parts) {
    const grooves: GrooveAssignment = p.grooves;
    for (const side of SIDES) {
      if (grooves[side] !== '') mm += sideLength(p, side) * p.qty;
    }
  }
  return mm / 1000;
}

/** Все метрики инфо-панели разом (сырые числа, без округления). */
export interface PartsSummary {
  /** Площадь деталей, м². */
  area: number;
  /** Кромка 1 (type1), м. */
  edge1: number;
  /** Кромка 2 (type2), м. */
  edge2: number;
  /** Пазы (любой тип), м. */
  grooves: number;
}

export function computePartsSummary(parts: Part[]): PartsSummary {
  return {
    area: partsArea(parts),
    edge1: edgeLength(parts, 'type1'),
    edge2: edgeLength(parts, 'type2'),
    grooves: grooveLength(parts),
  };
}
